import { app, authentication } from '@microsoft/teams-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { useAddMisconnectedCustomersDialogPageStyles } from './AddMisconnectedCustomersDialogPage.styles';
import { Button, Label, Spinner, Textarea } from '@fluentui/react-components';
import {
    isEmptyCustomer,
    MisconnectedCustomerInput,
    parseTime,
    validateCustomer,
} from './AddMisconnectedCustomersDialogPage.utils';
import { IBotSubmitPayload } from '../../model/IBotSubmitPayload';
import {
    IMisconnectedCustomersData,
    IMisconnectedFlight,
} from '../../model/IMisconnectedCustomersData';
import BotSubmitAction from '../../model/BotSubmitAction';
import { submitDialog } from '../../utils/teams';

const AddMisconnectedCustomersDialogPage: React.FC = () => {
    const { flightTeamId } = useParams();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const styles = useAddMisconnectedCustomersDialogPageStyles();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rawDetails, setRawDetails] = useState('');
    const [parsedDetails, setParsedDetails] = useState<
        MisconnectedCustomerInput[]
    >([]);
    const [validationMessage, setValidationMessage] = useState('');

    useEffect(() => {
        app.notifySuccess();
    }, []);

    const disableSubmit = useMemo(() => {
        return (
            isSubmitting ||
            validationMessage.length > 0 ||
            parsedDetails.length === 0
        );
    }, [isSubmitting, validationMessage, parsedDetails.length]);

    const onDetailsChange = useCallback(
        (value: string) => {
            const maxLength = 4000;

            const fail = (error: string) => {
                setValidationMessage(error);
                setParsedDetails([]);
            };

            setRawDetails(value || '');

            const text = value?.trim() || '';

            if (text.length === 0) {
                setParsedDetails([]);
                setValidationMessage('');
                return;
            } else if (text.length > maxLength) {
                fail(
                    t('MisconnectedCustomersValidation_TooLong').replace(
                        '{length}',
                        maxLength.toString()
                    )
                );
                return;
            }

            let parsed: MisconnectedCustomerInput[] | undefined;
            try {
                parsed = JSON.parse(text) as MisconnectedCustomerInput[];
            } catch {
                parsed = undefined;
            }

            if (
                parsed === undefined ||
                !Array.isArray(parsed) ||
                parsed.length === 0
            ) {
                fail(t('MisconnectedCustomersValidation_ParsingError'));
                return;
            }

            const nonEmptyCustomers = parsed.filter((x) => !isEmptyCustomer(x));

            if (nonEmptyCustomers.length === 0) {
                fail(t('MisconnectedCustomersValidation_NoCustomers'));
                return;
            }

            for (let i = 0; i < nonEmptyCustomers.length; i++) {
                const isValid = validateCustomer(nonEmptyCustomers[i]);

                if (!isValid) {
                    fail(
                        t(
                            'MisconnectedCustomersValidation_ValidationError'
                        ).replace('{row}', (i + 1).toString())
                    );
                    return;
                }
            }

            setParsedDetails(nonEmptyCustomers);
            setValidationMessage('');
        },
        [setRawDetails, setParsedDetails, setValidationMessage, t]
    );

    // Handle prefill (now safely after onDetailsChange is declared)
    useEffect(() => {
        const prefill = searchParams.get('prefill');
        if (prefill) {
            setRawDetails(prefill);
            onDetailsChange(prefill);
        }
    }, [searchParams, onDetailsChange]);

    const onSubmit = useCallback(async () => {
        if (
            isSubmitting ||
            validationMessage.length > 0 ||
            parsedDetails.length === 0
        ) {
            return;
        }

        setIsSubmitting(true);

        const flights: IMisconnectedFlight[] = parsedDetails.map((x) => {
            return {
                customer: {
                    firstName: x.firstName,
                    lastName: x.surname,
                    seatNumber: x.seatNumber,
                    cabin: x.cabin,
                    frequentFlyerTier: x.tier,
                },
                numberOfPassengers: x.passengerCount
                    ? Number(x.passengerCount)
                    : undefined,
                flightIata: x.flightIata,
                departureAirportIata: x.departureAirport,
                arrivalAirportIata: x.destinationAirport,
                transitAirportsIata: x.transitPoint,
                scheduledDepartureDate: x.departureDate || undefined,
                scheduledDepartureTime: parseTime(x.departureTime)!,
                scheduledArrivalDate: x.arrivalDate || undefined,
                scheduledArrivalTime: parseTime(x.arrivalTime),
                overnightStay: x.overnightStay === 'Y',
                notes: x.notes,
            };
        });

        const token = await authentication.getAuthToken();

        const payload: IBotSubmitPayload<IMisconnectedCustomersData> = {
            data: {
                flightTeamId: flightTeamId!,
                flights,
            },
            appUserAccessToken: token,
            action: BotSubmitAction.MisconnectedCustomers,
        };

        submitDialog(payload);
    }, [
        isSubmitting,
        setIsSubmitting,
        validationMessage,
        parsedDetails,
        flightTeamId,
    ]);

    return (
        <div className={styles.root}>
            <div className={styles.formField}>
                <Label weight="semibold" required>
                    {t('MisconnectedCustomerDetails')}
                </Label>
                <Textarea
                    rows={5}
                    value={rawDetails}
                    onChange={(_, data) => onDetailsChange(data.value)}
                    placeholder={t('MisconnectedCustomersTextPlaceholder')}
                />
                {validationMessage && (
                    <Label size="small" className={styles.danger}>
                        {validationMessage}
                    </Label>
                )}
            </div>
            <div className={styles.actions}>
                <Button
                    disabled={disableSubmit}
                    appearance="primary"
                    icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
                    iconPosition="before"
                    onClick={onSubmit}
                >
                    {t('Submit')}
                </Button>
            </div>
        </div>
    );
};

export default AddMisconnectedCustomersDialogPage;
